import { FUNCTIONS_ENDPOINT, IS_WEB, RECORD } from "./config";
import { XLINKS } from "./constants";
import { throwErrorWithStack } from "./crashlytics";
import {
  replayCommunityDialogEl,
  replayCommunityDialogPreviewEl,
  replayDialogEl,
  replayDialogPreviewEl,
  replayFavIconEl,
} from "./elements";
import { Filesystem, Share } from "./plugins";
import { withSpinner } from "./spinner";
import store from "./store";
import { Directory } from "@capacitor/filesystem";
import { runAfterNextRepaint } from "./utils";
import { GAME_MODE, gameState, levelDef } from "./game/gameState";
import { logEvent } from "./analytics";
import { communityLevelWonAddReviewBtns, setFavorite } from "./community/level";
import { getLogger } from "./logging";

const log = getLogger("📹 RecordGame");

let replayingGlob = false;

export const replayGame = () => {
  if (replayingGlob) {
    log.error("Already replaying");
    return;
  }
  replayingGlob = true;
  store.gameFramesReplaying = [...store.gameFrames];
  let currentIndex = 0;
  const bgEl = document.createElement("div");
  bgEl.style.position = "absolute";
  bgEl.style.width = "100%";
  bgEl.style.height = "100%";
  bgEl.style.background =
    "url(img/bg/game.jpg) center center / cover rgb(187, 178, 172)";
  const previewEl = [GAME_MODE.STORY, GAME_MODE.CLASSIC].includes(
    gameState.mode
  )
    ? replayDialogPreviewEl
    : replayCommunityDialogPreviewEl;
  function displayNextFrame() {
    if (!store.gameFramesReplaying.length) {
      replayingGlob = false;
      return;
    }
    if (currentIndex >= store.gameFramesReplaying.length) currentIndex = 0;
    previewEl.innerHTML = "";
    const fragment = document.createDocumentFragment();
    fragment.appendChild(bgEl);
    fragment.appendChild(store.gameFramesReplaying[currentIndex]);
    previewEl.appendChild(fragment);
    currentIndex++;
    setTimeout(displayNextFrame, store.fastPlayback ? 150 : 300);
  }
  if (gameState.mode == GAME_MODE.COMMUNITY) {
    const isFavorite = store.levelsFavorited.has(levelDef.id);
    setFavorite(isFavorite);
    if (isFavorite && replayFavIconEl.classList.contains("outlined"))
      replayFavIconEl.classList.remove("outlined");
    else replayFavIconEl.classList.add("outlined");
    if (
      Object.prototype.hasOwnProperty.call(
        store.communityLevelsRatings,
        levelDef.id
      )
    )
      communityLevelWonAddReviewBtns(
        store.communityLevelsRatings[levelDef.id]
      )();
  }
  ([GAME_MODE.STORY, GAME_MODE.CLASSIC].includes(gameState.mode)
    ? replayDialogEl
    : replayCommunityDialogEl
  ).style.display = "initial";
  displayNextFrame();
};

function selectiveDeepClone(element, elementsToRemove) {
  if (element.tagName.toLowerCase() === "style") return element.cloneNode(true);
  const clone = element.cloneNode(false);
  for (const child of element.children) {
    if (!elementsToRemove.includes(child.id))
      clone.appendChild(selectiveDeepClone(child, elementsToRemove));
  }
  return clone;
}

function _saveSvgFrame(el) {
  const elementsToRemove = [
    "boosters",
    "indicators-bg",
    "map",
    "lock",
    "cat-episodes",
    "paws",
    "pointer",
    "helper-pointer",
    "cell-selector",
  ];
  const svgEl = selectiveDeepClone(el, elementsToRemove);
  const helperCells = Array.from(
    svgEl.querySelectorAll("use.helper-cell")
  ).filter((el) => el.getAttributeNS(XLINKS, "href") === "#helper-cell");
  helperCells.forEach((helperCell) =>
    helperCell.setAttributeNS(XLINKS, "xlink:href", "#free-cell")
  );
  const winCells = Array.from(svgEl.querySelectorAll("use.win-cell"));
  winCells.forEach((winCell) =>
    winCell.setAttributeNS(XLINKS, "xlink:href", "#free-cell")
  );
  svgEl.style.position = "absolute";
  svgEl.style.top = "0";
  svgEl.style.left = "0";
  svgEl.style.width = "100%";
  svgEl.style.height = "100%";
  svgEl.style.padding = 0;
  svgEl.removeAttribute("width");
  svgEl.removeAttribute("height");
  svgEl.setAttribute("viewBox", "0 0 530 395");
  const boardElement = svgEl.querySelector("#board");
  svgEl.style.filter = "none";

  const match = boardElement
    .getAttribute("transform")
    .match(/translate\(([-\d.]+)\s+([-\d.]+)\)/);
  if (match) {
    const newX = parseFloat(match[1]) / 2;
    // const newX = 0;
    const newY = parseFloat(match[2]) / 2;
    const updatedTransform = boardElement
      .getAttribute("transform")
      .replace(/translate\([\d\s.-]+\)/, `translate(${newX} ${newY})`);
    boardElement.setAttribute("transform", updatedTransform);
  }
  store.gameFrames.push(svgEl);
}

export function saveSvgFrame(el, afterNextRepaint=false) {
  if (!RECORD) return;
  if (afterNextRepaint) runAfterNextRepaint(_saveSvgFrame, el);
  else _saveSvgFrame(el);
}

// cache
let _lastBody = null;
let _lastResp = null;

export async function convertSvgsTo({ type, ...options } = {}) {
  const data = [];
  for (const svgEl of store.gameFramesReplaying)
    data.push(new XMLSerializer().serializeToString(svgEl));

  const response = await convertSvg({
    functionName: `convert_svgs_to_${type}`,
    data,
    ...options,
  });
  const blob = await response.blob();
  return blob;
}

export async function convertSvg({ functionName, data, ...options } = {}) {
  const body = JSON.stringify({
    data,
    duration: store.fastPlayback ? 150 : 300,
    ...options,
  });
  if (_lastBody === body && !(IS_WEB && __DEV__)) {
    log.debug("Using cached response");
    return _lastResp;
  }
  const functionsEndpoint = FUNCTIONS_ENDPOINT.replace(
    "{function-name}",
    functionName.replace(/_/g, "-")
  );
  const response = await fetch(functionsEndpoint + functionName, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body,
  });
  if (!response.ok) {
    let errorText = "";
    try {
      errorText = await response.text();
    } catch (e) {
      log.error(e);
    }
    throw new Error(`Fetch failed: ${response.status} ${errorText}`);
  }
  _lastBody = body;
  _lastResp = response.clone();
  return response;
}

export function downloadBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  document.body.appendChild(a);
  a.style = "display: none";
  a.href = url;
  a.download = name;
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export async function shareGame() {
  let blob;
  try {
    blob = await withSpinner(
      convertSvgsTo({ type: "video" }),
      "Converting replay to video...<br>This may take a minute or so."
    );
  } catch (error) {
    throwErrorWithStack(error, true);
    return;
  }
  try {
    if (IS_WEB) {
      downloadBlob(blob, "game.mp4");
      return;
    }
    let blobUrl = await withSpinner(writeBlobToFile(blob));
    const shareObject = {
      title: "Watch My Epic Game Replay!",
      text: "Hey! I just had an awesome game on Trap the Cat and wanted to share my replay with you. Also, you can download the game here and join the fun:\nhttps://play.google.com/store/apps/details?id=com.kima.chatnoirhex",
      url: blobUrl,
      dialogTitle: "Share Your Epic Trap the Cat Replay!",
    };
    const result = await Share.share(shareObject);
    log.debug(result);
    logEvent("share_replay");
  } catch (error) {
    if (error.message != "Share canceled") throwErrorWithStack(error);
  }
}

async function writeBlobToFile(blob, fileName = "epicGameReply.mp4") {
  // Convert blob to base64
  const reader = new FileReader();
  reader.readAsDataURL(blob);
  const base64Data = await new Promise((resolve, reject) => {
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
  });

  // Write the base64 data to a file
  const savedFile = await Filesystem.writeFile({
    path: fileName,
    data: base64Data,
    directory: Directory.External,
  });
  return `file://${savedFile.uri}`;
}

export const setSpeedControl = (replaySpeedControlBtnEl) => {
  replaySpeedControlBtnEl.style.transform = store.fastPlayback
    ? "scaleX(-1)"
    : null;
};
